import { User } from '../types';

const norm = (role?: string | null) => role?.toLowerCase() ?? '';

export const userRoles = (user?: User | null) => {
  const listed = user?.roles ?? [];
  const single = user?.role ? [user.role] : [];
  return [...new Set([...listed, ...single].map((role) => norm(role)).filter(Boolean))];
};

export const hasRole = (user: User | null | undefined, ...names: string[]) => {
  const roles = userRoles(user);
  if (roles.includes('owner')) return true;
  return names.some((name) => {
    const key = name.toLowerCase();
    if (key === 'mentor') return roles.includes('mentor') || roles.includes('instructor');
    if (key === 'courseowner') return roles.includes('courseowner') || roles.includes('coursecreator');
    return roles.includes(key);
  });
};

/** Owner is the platform super-admin and inherits every capability. */
export const isOwner = (user?: User | null) => hasRole(user, 'owner');
export const isSuperAdmin = isOwner;

export const isCourseOwner = (user?: User | null) => hasRole(user, 'courseowner');
/** @deprecated Use isCourseOwner. */
export const isCourseCreator = isCourseOwner;
export const isContentAdmin = (_user?: User | null) => false;
export const isAuthor = (_user?: User | null) => false;
export const isReviewer = (_user?: User | null) => false;

export const isCourseAdmin = (user?: User | null) => hasRole(user, 'courseadmin');
export const isGigAdmin = (user?: User | null) => hasRole(user, 'gigadmin');
export const isChallengeAdmin = (user?: User | null) => hasRole(user, 'challengeadmin');
export const isUserAdmin = (user?: User | null) => hasRole(user, 'useradmin');
export const isAcademyAdmin = (user?: User | null) => hasRole(user, 'academyadmin');
export const isInterviewAdmin = (user?: User | null) => hasRole(user, 'interviewadmin');
export const isMentor = (user?: User | null) => hasRole(user, 'mentor');
export const isInterviewer = (user?: User | null) => hasRole(user, 'interviewer');

export const isAdmin = (user?: User | null) =>
  isCourseAdmin(user) || isGigAdmin(user) || isChallengeAdmin(user) || isUserAdmin(user) || isAcademyAdmin(user) || isInterviewAdmin(user);

export const canWriteCourses = (user?: User | null) => isCourseOwner(user) || isCourseAdmin(user);
export const canWriteLabs = (user?: User | null) => canWriteCourses(user);
export const canWriteProjects = (user?: User | null) => canWriteCourses(user);
export const canWriteGigs = (user?: User | null) => isGigAdmin(user) || hasRole(user, 'gigcreator');
export const canWriteEvents = (user?: User | null) => isOwner(user);
/** CourseAdmin reviews; Owner also inherits via hasRole. */
export const canReview = (user?: User | null) => isCourseAdmin(user);
export const canReviewProjects = canReview;
export const canReviewGigs = (user?: User | null) => isGigAdmin(user);
export const canReviewEvents = (user?: User | null) => isOwner(user);
/** Final approve & publish for courses/projects/labs — platform Owner only. */
export const canApprove = (user?: User | null) => isOwner(user);
/** Owner direct-publishes courses; GigAdmin can still direct-publish gigs. */
export const canDirectPublish = (user?: User | null) => isOwner(user) || isGigAdmin(user);
export const canManageContent = (user?: User | null) => isOwner(user);
export const isProjectAdmin = (user?: User | null) => isCourseAdmin(user);
export const canManageUsers = (user?: User | null) => isUserAdmin(user);
export const canViewEnrollmentReports = (user?: User | null) => isCourseAdmin(user);
export const canManageInterviews = (user?: User | null) => isInterviewAdmin(user);
export const canApproveInterviewers = (user?: User | null) => isInterviewAdmin(user);
export const canApproveMentors = (user?: User | null) => isAcademyAdmin(user);
export const canAssignProgramMentors = (user?: User | null) => isAcademyAdmin(user);
export const canAccessAdmin = (user?: User | null) =>
  isAdmin(user) || isCourseOwner(user) || canWriteGigs(user);
export const submitMessage = (user?: User | null) => {
  if (isOwner(user)) return 'Published.';
  if (isCourseAdmin(user)) return 'Submitted for Owner final approval.';
  if (isGigAdmin(user)) return 'Published.';
  return 'Submitted for Course Admin review, then Owner publish approval.';
};
